import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { DescriptionFormat, GalaxyLink } from '@/types/galaxy-data/unit'

/**
 * General settings for our apps
 * It contains for example if the rates should show decimals,
 * the style for short descriptions (short/truncated/..) etc
 * */
export const useConfigurations = defineStore('config', () => {
  /** These settings can be available on several apps */
  const general = ref<{
    firstDayOfTheWeek?: 'Monday' | 'Sunday'
    privacyLink?: string
    dateFormat?:
      | 'MM/dd/yyyy'
      | 'dd/MM/yyyy'
      | 'EEE, MMM dd, yyyy'
      | 'MMM dd, yyyy'
      | 'dd MMM, yyyy'
      | 'EEE, MMM dd'
      | 'MMM dd'
      | 'dd MMM'
    warnUnload: boolean
    payPercentage: number
    rateDisplaySwitch: boolean
    rateDisplay: string
    startEndTimeDisplay: number
  }>({})

  const hotelSearch = ref<{
    shortDescriptionDisplay?: DescriptionFormat
    showBrandLogo?: boolean
    showHotelAttributes?: boolean
    showMapView?: boolean
    showSort?: boolean
    requestAQuoteLink?: GalaxyLink
    showFilters?: boolean
    showFilterByBrand?: boolean
    showFilterByEventType?: boolean
    showFilterByEventRoomCapacity?: boolean
    showFilterByEventRoomSize?: boolean
    showFilterByEventRoomSetup?: boolean
    showFilterByCategory?: boolean
    showFilterByStars?: boolean
    defaultNbAttendees: number
    hotelsLayout: number
    locationIcon: number
  }>({})

  const funnel = ref<{
    /** URl of the page "confirmation" where the user will be redirected */
    confirmationUrl?: string
    /** DB2 Location ID */
    locationId?: string
    /** WYSWYG content - Terms & conditions */
    termsAndConditions?: string
    showHotelCard?: boolean
    /** Contact URL */
    requestAQuoteLink?: GalaxyLink
    minDate?: Date
    maxDate?: Date
    defaultNbAttendees: number
    /**
     * If the step "Select your venue" is enabled
     * after selecting a package, the user can
     * choose their venue
     * */
    showVenueSelection?: boolean
    showGuestrooms?: boolean
    /** Information required for the proxy to perform partial deposit */
    proxyDataObjectId?: string
    proxyDataObjectType?: string
    /** Progress Bar Style */
    progressBarStyle?: 1 | 2
  }>({})

  const packageList = ref<{
    showSort?: boolean
    shortDescriptionDisplay?: DescriptionFormat
    showFilters?: boolean
    showFilterFunctionRoomSetup?: boolean
    showFilterEventType?: boolean
    noPackageImageUrl?: string
  }>({})

  const venueList = ref<{
    showSort?: boolean
    shortDescriptionDisplay?: DescriptionFormat
    showRoomAttributes?: boolean
    showRoomAmenities?: boolean
    showFilters?: boolean
    showFilterByAmenities?: boolean
    showFilterByRoomSize?: boolean
  }>({})

  const abandonmentPopup = ref<{
    enabled?: boolean
    delaySeconds?: number
    contactLink?: GalaxyLink
  }>({})

  const confirmationPage = ref<{
    cancellation_policy?: string
  }>({})

  /**
   * This is called after initializing the booking funnel
   * and will check that the basic configuration is here
   */
  function checkFunnelErrors() {
    const errors = []
    if (!funnel.value.confirmationUrl) {
      errors.push('Confirmation page')
    }
    if (!funnel.value.locationId) {
      errors.push('Hotel Location ID')
    }
    if (!funnel.value.termsAndConditions) {
      errors.push('Terms & conditions')
    }
    return errors
  }
  const resetIdleValue = ref(false)
  const disableIdleValue = ref(false)
  const isEventConfigurable = ref(true)

  function resetIdle() {
    resetIdleValue.value = true
  }

  function disableIdle() {
    disableIdleValue.value = true
  }

  return {
    general,

    hotelSearch,
    funnel,
    packageList,
    venueList,
    abandonmentPopup,
    confirmationPage,

    checkFunnelErrors,
    resetIdle,
    disableIdle,
    resetIdleValue,
    disableIdleValue,
    isEventConfigurable,
  }
})
